// ─────────────────────────────────────────────────────────────────────────────
// prettifyDocumentName — cleans up the long stored filename for display.
//
// Backend stamps uploaded filenames with a trailing _<unix-timestamp> to
// disambiguate duplicate uploads on the filesystem. The .pdf icon already
// signals it's a PDF, so the extension is redundant in lists. URL-encoded
// spaces (%20) sometimes leak through in legacy rows. This helper:
//
//   1. URL-decodes any %xx escapes (best-effort; falls back to raw on error).
//   2. Strips a trailing _<10-13 digit millisecond timestamp> suffix.
//   3. Strips a trailing copy-counter like " (1)".
//   4. Strips the file extension (.pdf, .png, .jpg, .jpeg, .gif, .webp,
//      .doc, .docx, .xlsx).
//   5. Trims surrounding whitespace.
//
// Examples:
//   "PRIMARY%20-%20NFC%20GRIT%20BLASTING%20MACHINE%20SHED_1775423044274.pdf"
//     → "PRIMARY - NFC GRIT BLASTING MACHINE SHED"
//   "ST_766 Dean St_S-100 CD.pdf"
//     → "ST_766 Dean St_S-100 CD"
//   "drawing (1).pdf"
//     → "drawing"
//
// Intentionally conservative on the timestamp pattern: requires 10–13 digits
// so it won't strip legitimate piece-mark numbers like "W12X19" or "S-100".
// ─────────────────────────────────────────────────────────────────────────────

// Trailing _<10-13 digit timestamp> right before the extension (or at end)
const TRAILING_UPLOAD_TIMESTAMP = /_\d{10,13}(?=(\.[A-Za-z0-9]{2,5})?$)/;

// Extensions we recognize as redundant in display
const EXTENSION = /\.(pdf|png|jpg|jpeg|gif|webp|doc|docx|xls|xlsx|svg|tif|tiff)$/i;

// Trailing copy-counter " (1)" / " (2)" / etc.
const COPY_COUNTER = /\s*\(\d+\)\s*$/;

/**
 * Clean up a single filename string for human display.
 * @param {string} raw — the stored filename (file_name / original_name / etc.)
 * @returns {string} the cleaned, display-ready name
 */
export function prettifyFilename(raw) {
  if (!raw || typeof raw !== "string") return "";
  let name = raw;
  // Best-effort URL-decode (legacy rows sometimes carry %20 escapes).
  try {
    if (name.includes("%")) name = decodeURIComponent(name);
  } catch (_e) {
    // leave raw if malformed
  }
  // Order matters: timestamp BEFORE extension strip (it's anchored against the
  // extension or end-of-string), then extension, then copy-counter, then trim.
  name = name.replace(TRAILING_UPLOAD_TIMESTAMP, "");
  name = name.replace(EXTENSION, "");
  name = name.replace(COPY_COUNTER, "");
  return name.trim();
}

/**
 * Pick the best filename field off a document object and prettify it.
 * Mirrors the field-fallback chain used across the app.
 *
 * Falls back to "Document #<id>" when no name field exists, or "(unnamed)"
 * when there's no id either (rare — implies the row is malformed).
 *
 * @param {object} doc — document row from the API
 * @returns {string} display-ready name
 */
export function prettifyDocumentName(doc) {
  if (!doc) return "(unnamed)";
  const raw =
    doc.file_name ||
    doc.filename ||
    doc.original_name ||
    doc.originalName ||
    doc.stored_name ||
    doc.storedName ||
    "";
  const cleaned = prettifyFilename(raw);
  if (cleaned) return cleaned;
  if (doc.id != null) return `Document #${doc.id}`;
  return "(unnamed)";
}

export default prettifyDocumentName;
